import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alerta, Botao, Cartao, Tabela, quantidadeComUnidade } from "@plantus/ui";
import type { Coluna } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type { VolumeDaEtiqueta } from "@plantus/api-client";

import { RECIPIENTE_NAO_IDENTIFICADO } from "../recipientes/estadoDoRecipiente";

/**
 * A aba **Volumes** — os recipientes deste lote, um por linha.
 *
 * <h2>Ela existe porque o dado existe, e porque faltava um caminho</h2>
 *
 * <p>`GET /lotes/{id}/volumes` já devolve, por recipiente, o número, o peso
 * gravado, a plaqueta do tambor e quantas etiquetas já saíram. Até a #36 esse
 * dado só aparecia **dentro da grade de escolha** da tela de etiquetas, onde a
 * pergunta é outra ("quais vão para o papel"), e a ficha do volume respondia
 * sobre **um** recipiente de cada vez.
 *
 * <p>Era pendência escrita na feature 1.2.3: *"Não há caminho para os demais
 * volumes do mesmo lote a partir daqui"*.
 *
 * <h2>Consulta, e só</h2>
 *
 * <p>Nada aqui emite, edita ou vincula. O único caminho que sai desta aba é a
 * **ficha do volume** — a mesma tela que o QR do tambor abre —, e ela também é
 * de consulta. Emitir continua sendo ato da aba de etiquetas, com escolha
 * explícita e motivo quando for reimpressão.
 */
export function AbaVolumes({
  empresaId,
  loteId,
  unidade,
}: {
  empresaId: string;
  loteId: string;
  /** O símbolo já pronto do lote — `kg`, `L`. O volume não o repete. */
  unidade: string;
}) {
  const navegar = useNavigate();
  const [volumes, definirVolumes] = useState<VolumeDaEtiqueta[] | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    definirErro(null);
    producao
      .volumesDoLote(empresaId, loteId)
      .then(definirVolumes)
      .catch(() => definirErro("Não foi possível carregar os volumes deste lote."));
  }, [empresaId, loteId]);

  if (erro !== null) return <Alerta tom="erro">{erro}</Alerta>;
  if (volumes === null) return <p role="status">Carregando volumes…</p>;

  const comEtiqueta = volumes.filter((v) => v.viasEmitidas > 0).length;
  const semPlaqueta = volumes.filter(
    (v) => v.recipiente === null || v.recipiente === "",
  ).length;

  return (
    <div className="lote-volumes">
      <Cartao titulo="Volumes">
        <h2 className="lote__rotulo-secao">Volumes</h2>

        {/* A frase afirma o CONJUNTO, e é calculada dele: um texto fixo
            ("todos etiquetados") vira mentira em silêncio na primeira
            divergência — o mesmo defeito da legenda da tabela de lotes. */}
        {/* ⚠️ O ZERO VIRA PALAVRA. "0 já têm etiqueta emitida" não é português,
            e um sistema que não sabe concordar o próprio plural parece
            rascunho justo na tela em que se confere material — é a mesma
            correção que o "1 etiqueta(s)" da folha já pagou. */}
        <p className="lote-volumes__resumo">
          {volumes.length} {volumes.length === 1 ? "recipiente" : "recipientes"} ·{" "}
          <strong>
            {comEtiqueta === 0
              ? "nenhum tem etiqueta emitida"
              : `${comEtiqueta} ${comEtiqueta === 1 ? "já tem" : "já têm"} etiqueta emitida`}
          </strong>
          {semPlaqueta > 0 && (
            <>
              {" "}
              ·{" "}
              {semPlaqueta === volumes.length
                ? "nenhum com plaqueta lida"
                : `${semPlaqueta} sem plaqueta lida`}
            </>
          )}
          .
        </p>

        <Tabela
          legenda="Recipientes deste lote, com o peso gravado de cada um e quantas etiquetas já saíram."
          linhas={volumes}
          chave={(v) => String(v.numero)}
          colunas={colunas(navegar, unidade)}
        />
      </Cartao>
    </div>
  );
}

function colunas(
  navegar: (rota: string) => void,
  unidade: string,
): Coluna<VolumeDaEtiqueta>[] {
  return [
    {
      chave: "numero",
      rotulo: "Volume",
      principal: true,
      mono: true,
      conteudo: (v) => String(v.numero),
    },
    {
      chave: "quantidade",
      rotulo: "Peso",
      numerica: true,
      // `quantidadeComUnidade` e nunca o texto cru: `47.185000 kg` se lê como
      // quarenta e sete MIL em português. É a mesma função da etiqueta, porque
      // tela e papel discordarem sobre um peso é pior que os dois errarem juntos.
      conteudo: (v) => quantidadeComUnidade(v.quantidade, unidade),
    },
    {
      chave: "recipiente",
      rotulo: "Recipiente",
      // 🔴 Ausência NOMEADA, não célula vazia. Espaço em branco se lê como "o
      // dado existia e alguém esqueceu"; aqui significa o contrário — o vínculo
      // nunca foi feito, porque não há plaqueta no tambor para ler
      // (ADR-0012 §8.4). Mesmo princípio do `SeloDeEmpresa` sem empresa.
      conteudo: (v) =>
        v.recipiente !== null && v.recipiente !== "" ? (
          <span className="lote-volumes__plaqueta">{v.recipiente}</span>
        ) : (
          <span className="lote-volumes__ausente">{RECIPIENTE_NAO_IDENTIFICADO}</span>
        ),
    },
    {
      chave: "viasEmitidas",
      rotulo: "Etiquetas",
      numerica: true,
      // O número é o que o servidor sabe e a pessoa não: quantas vias daquele
      // recipiente já saíram. Zero vira palavra — um `0` numa coluna de
      // contagem se lê como "nenhuma ainda" só depois de pensar.
      conteudo: (v) =>
        v.viasEmitidas === 0 ? (
          <span className="lote-volumes__sem-via">nenhuma</span>
        ) : (
          `${v.viasEmitidas} ${v.viasEmitidas === 1 ? "via" : "vias"}`
        ),
    },
    {
      chave: "acao",
      rotulo: "Ações",
      rotuloOculto: true,
      // O destino é a MESMA tela que o QR do tambor abre. Não é uma segunda
      // ficha: duas telas para "o que é este volume" divergiriam, e a que
      // ninguém está olhando é sempre a que fica para trás (1.2.3 R2).
      conteudo: (v) => (
        <Botao
          aparencia="texto"
          onClick={() => navegar(`/producao/materias-primas/volume/${v.token}`)}
        >
          Ficha
        </Botao>
      ),
    },
  ];
}
